/*
 * Shared MySchoolRx public chrome — header, footer and page shell.
 *
 * Replicates the sticky nav + footer from CaporilliLanding so the dedicated
 * footer pages (About / Contact / Privacy / Terms) match the public landing
 * page exactly: same logo, typography, #007a9e accent, borders and announcement
 * bar (GlobalMessageBar is rendered globally in App.js). Nav anchor links point
 * back to the landing page sections so visitors are never sent to unrelated
 * destinations.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
export const ROUTE = "/myschoolrx";
export const BLUE = "#007a9e";
export const BLUE_HOVER = "#006583";

const NAV_LINKS = [
  { label: "For Schools", to: `${ROUTE}/schools` },
  { label: "For Portfolios", to: `${ROUTE}/portfolios` },
  { label: "How It Works", to: `${ROUTE}#how-it-works` },
  { label: "Research & Experts", to: `${ROUTE}/experts` },
  { label: "FAQs", to: `${ROUTE}#faqs` },
];

/* Preserves the existing enrollment destination (landing-cta suffix). */
export const useLandingCta = () => {
  const [cta, setCta] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/landing-cta`).then((r) => setCta(r.data)).catch(() => {});
  }, []);
  return cta;
};

export const ctaHref = (cta) => `${ROUTE}/${cta?.destination_suffix || "get-started"}`;

export const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleRef = useRef(null);
  const closeRef = useRef(null);
  const cta = useLandingCta();
  const isActive = (to) => to.startsWith(ROUTE) && !to.includes("#") && location.pathname === to;
  const goCta = () => navigate(ctaHref(cta));
  const closeMenu = () => { setMobileOpen(false); if (toggleRef.current) toggleRef.current.focus(); };
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeMenu(); };
    document.addEventListener("keydown", onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [mobileOpen]);
  return (
    <>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#e5e7eb]" data-testid="public-topbar">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex items-center justify-between h-[72px] gap-6">
          <a href={ROUTE} className="flex items-center shrink-0" data-testid="public-topbar-logo">
            <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-7 w-auto max-sm:h-auto max-sm:max-w-[140px]" />
          </a>
          <nav className="hidden lg:flex items-center gap-7" aria-label="Main" data-testid="public-topbar-nav">
            {NAV_LINKS.map((l) => (
              <a
                key={l.to}
                href={l.to}
                aria-current={isActive(l.to) ? "page" : undefined}
                className={`whitespace-nowrap text-[17px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007a9e]/40 rounded ${isActive(l.to) ? "text-[#007a9e] underline decoration-2 underline-offset-[6px]" : "text-[#374151] hover:text-[#111827]"}`}
                data-testid={`public-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <a
              href="/diagnostic/login"
              className="hidden sm:inline-flex text-[17px] font-semibold text-[#374151] hover:text-[#111827] transition-colors"
              data-testid="public-topbar-signin"
            >
              Sign in
            </a>
            <span className="hidden sm:block h-5 w-px bg-[#e5e7eb]" />
            <Button
              onClick={goCta}
              className="hidden sm:inline-flex h-9 text-white text-[17px] font-semibold px-4"
              style={{ backgroundColor: BLUE }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
              data-testid="public-topbar-cta"
            >
              Become a Founding Partner
            </Button>
            <button
              type="button"
              ref={toggleRef}
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-[#e5e7eb] text-[#111827] hover:bg-[#f9fafb]"
              aria-label="Open menu"
              aria-expanded={mobileOpen ? "true" : "false"}
              aria-controls="public-mobile-menu"
              data-testid="public-topbar-hamburger"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" id="public-mobile-menu" data-testid="public-mobile-menu" role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={(e) => { if (e.key === "Escape") closeMenu(); }}>
          <div className="absolute inset-0 bg-black/50" onClick={closeMenu} />
          <div className="absolute top-0 right-0 h-full w-[300px] max-w-[88vw] bg-white shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between h-16 px-4 border-b border-[#e5e7eb] shrink-0">
              <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-7 w-auto max-w-[140px]" />
              <button
                type="button"
                ref={closeRef}
                onClick={closeMenu}
                className="inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-[#f3f4f6]"
                aria-label="Close menu"
                data-testid="public-mobile-menu-close"
              >
                <X className="h-5 w-5 text-[#111827]" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1" data-testid="public-mobile-nav">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.to}
                  href={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-3 rounded-md text-[19px] font-semibold text-[#111827] hover:bg-[#f9fafb]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/diagnostic/login"
                className="block px-3 py-3 mt-3 rounded-md text-[18px] font-semibold text-[#374151] border border-[#e5e7eb] text-center"
                data-testid="public-mobile-nav-signin"
              >
                Sign in
              </a>
              <Button
                onClick={() => { setMobileOpen(false); goCta(); }}
                className="mt-2 w-full h-11 text-white text-[18px] font-semibold"
                style={{ backgroundColor: BLUE }}
                data-testid="public-mobile-nav-cta"
              >
                Become a Founding Partner
              </Button>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export const PublicFooter = () => (
  <footer className="bg-white border-t border-[#e5e7eb]" data-testid="public-footer">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div className="text-[16px] text-[#6b7280]">
        <div data-testid="public-footer-operator">MySchoolRx is operated by 589 Labs LLC.</div>
        <div className="mt-1">© {new Date().getFullYear()} 589 Labs LLC. All rights reserved.</div>
      </div>
      <nav className="flex items-center gap-5 text-[16px]" aria-label="Footer" data-testid="public-footer-links">
        <a href="/about" className="text-[#374151] hover:text-[#111827]" data-testid="public-footer-about">About</a>
        <a href="/contact" className="text-[#374151] hover:text-[#111827]" data-testid="public-footer-contact">Contact</a>
        <a href="/privacy" className="text-[#374151] hover:text-[#111827]" data-testid="public-footer-privacy">Privacy</a>
        <a href="/terms" className="text-[#374151] hover:text-[#111827]" data-testid="public-footer-terms">Terms</a>
        <a href="/careers" className="text-[#374151] hover:text-[#111827]" data-testid="public-footer-careers-link">Careers</a>
      </nav>
    </div>
  </footer>
);

/* Page-specific title + description + canonical. Scroll-to-top is handled
   globally in App.js for these public routes. */
const usePageMeta = (title, description, canonicalPath) => {
  useEffect(() => {
    const canonical = `https://www.myschoolrx.com${canonicalPath}`;
    document.title = title;
    const setMeta = (sel, attr, key, val) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical);
  }, [title, description, canonicalPath]);
};

/* Scroll-reveal for section entrances (respects prefers-reduced-motion). */
export const useScrollReveal = () => {
  const location = useLocation();
  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const main = document.querySelector("main");
    if (!main) return undefined;
    const sections = Array.from(main.querySelectorAll("section")).slice(1);
    sections.forEach((s) => s.classList.add("reveal-on-scroll"));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("reveal-in"); io.unobserve(e.target); } });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.06 });
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [location.pathname]);
};

/* Floating "proof label" badge for illustrations (desktop only). */
export const ProofLabel = ({ icon: Icon, title, sub, className = "" }) => (
  <div className={`absolute z-10 hidden lg:flex items-center gap-2.5 rounded-xl border border-[#e5e7eb] bg-white px-3.5 py-2.5 shadow-lg shadow-black/10 ${className}`} aria-hidden="true">
    {Icon && <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: "#e6f4f8" }}><Icon className="h-4 w-4" style={{ color: BLUE }} /></span>}
    <span className="leading-tight"><b className="block text-[13px] font-bold text-[#0f172a]">{title}</b><span className="block text-[12px] text-[#6b7280]">{sub}</span></span>
  </div>
);

const PublicFaqItem = ({ item, idx }) => {
  const [open, setOpen] = useState(false);
  const panelId = `pfaq-${idx}`;
  const answer = Array.isArray(item.a) ? item.a : [item.a];
  return (
    <div className="rounded-lg border border-[#e5e7eb] bg-white transition-shadow hover:shadow-sm" data-testid={`public-faq-item-${idx}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={panelId} className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left">
        <span className="block text-[16px] leading-snug font-semibold text-[#0f172a]">{item.q}</span>
        <span className="shrink-0 mt-0.5 text-[#6b7280]" aria-hidden="true">{open ? <Minus className="h-5 w-5" /> : <Plus className="h-5 w-5" />}</span>
      </button>
      <div id={panelId} hidden={!open} className="px-5 pb-5 space-y-3 text-[15px] text-[#4b5563] leading-relaxed">{answer.map((p, j) => <p key={j}>{p}</p>)}</div>
    </div>
  );
};

/* Shared FAQ block matching the Landing page style. */
export const PublicFaq = ({ eyebrow = "FAQs", title, lead, items, allFaqsHref, id = "faqs", testid }) => (
  <section id={id} className="bg-[#f1f3f5] py-16 lg:py-24" data-testid={testid}>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-[0.75fr_1.6fr] gap-10 lg:gap-14 items-start">
        <div>
          <span className="text-[14px] uppercase tracking-[0.18em] font-semibold" style={{ color: BLUE }}>{eyebrow}</span>
          <h2 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#0f172a] leading-[1.05] tracking-tight">{title}</h2>
          {lead && <p className="mt-4 text-[16px] text-[#4b5563] leading-relaxed">{lead}</p>}
          {allFaqsHref && <p className="mt-4 text-[16px]"><a href={allFaqsHref} className="font-semibold hover:underline" style={{ color: BLUE }} data-testid="public-all-faqs-link">All FAQs →</a></p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {items.map((f, i) => <PublicFaqItem key={i} item={f} idx={i} />)}
        </div>
      </div>
    </div>
  </section>
);

export const PublicPageShell = ({ title, description, canonicalPath, children, testid }) => {
  usePageMeta(title, description, canonicalPath);
  useScrollReveal();
  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid={testid}>
      <PublicHeader />
      <main id="main" tabIndex={-1} ref={(el) => { if (el && el.id !== "main") el.id = "main"; }} className="flex-1">{children}</main>
      <PublicFooter />
    </div>
  );
};
